// Phase PACKER-1.1 · Packer dashboard
//   - "My Hub" card (hub_name primary + hub_id mono badge, lead, workers count)
//   - Brand-orange Scan button → PackerScanModal (QR/barcode + manual entry)
//   - Quick "Upload Proof" action on each pending/in_progress row
//   - KPI tiles + assigned-parcels list + 10s polling + new-parcel toast.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Loader2, RefreshCw, Package, Boxes, Clock, ScanLine, Building2,
  Users, CalendarCheck, Camera, Phone,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";
import useAuthStore from "../../store/authStore";
import PackerSubNav from "../../components/packer/PackerSubNav";
import PackerScanModal from "../../components/packer/PackerScanModal";

const ORANGE = "#FF6B00";

const STATUS_PILL = {
  pending:     { label: "Pending",     cls: "bg-slate-100 text-slate-700 border-slate-200" },
  in_progress: { label: "In Progress", cls: "bg-orange-100 text-[#C2410C] border-orange-200" },
  done:        { label: "Done",        cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

// Normalize the seeded "assigned" status from MHM-5.4 → "pending" for UI.
const norm = (s) => (s === "assigned" ? "pending" : (s || "pending"));

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function KpiCard({ label, value, accentOrange, testid, Icon }) {
  return (
    <div data-testid={testid}
         className={"flex-1 min-w-0 bg-white border rounded-xl px-3 py-2.5 transition-colors " +
                     (accentOrange
                       ? "border-[#FF6B00] shadow-[0_0_0_3px_rgba(255,107,0,0.10)]"
                       : "border-slate-200")}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={"h-3.5 w-3.5 " + (accentOrange ? "text-[#FF6B00]" : "text-slate-400")}/>
      </div>
      <div className={"text-2xl font-bold mt-0.5 font-['Outfit'] " + (accentOrange ? "text-[#FF6B00]" : "text-slate-900")}>
        {value ?? 0}
      </div>
    </div>
  );
}

function MyHubCard({ hub }) {
  // Hide gracefully if the packer isn't linked to a hub yet.
  if (!hub) return null;
  return (
    <section data-testid="packer-my-hub-card"
             className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-3.5 w-3.5" style={{ color: ORANGE }}/>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">My Hub</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-['Outfit'] text-base font-semibold text-slate-900 truncate"
              data-testid="packer-my-hub-name">
          {hub.hub_name || "Unnamed micro hub"}
        </span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
              data-testid="packer-my-hub-id"
              title="Micro hub ID">
          {hub.hub_id}
        </span>
      </div>
      {hub.hub_address && (
        <div className="text-xs text-slate-500 truncate mt-0.5"
             data-testid="packer-my-hub-address"
             title={hub.hub_address}>
          {hub.hub_address}
        </div>
      )}
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Lead</span>
          <span className="text-slate-700 truncate" data-testid="packer-my-hub-lead">
            {hub.lead_name || "—"}
          </span>
          {hub.lead_phone && (
            <span className="text-[11px] text-slate-500 font-mono inline-flex items-center gap-1">
              <Phone className="h-3 w-3"/> {hub.lead_phone}
            </span>
          )}
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Packers</span>
          <span className="text-slate-700 inline-flex items-center gap-1"
                data-testid="packer-my-hub-workers">
            <Users className="h-3 w-3"/>{hub.total_workers ?? 0}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Today</span>
          <span className="text-slate-700 inline-flex items-center gap-1"
                data-testid="packer-my-hub-today">
            <CalendarCheck className="h-3 w-3"/>{hub.hub_parcels_today ?? 0}
          </span>
        </div>
      </div>
    </section>
  );
}

function Body() {
  const [me, setMe] = useState(null);
  const [kpis, setKpis] = useState({ packed_today: 0, in_progress_now: 0, avg_pack_minutes_today: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [quickBusyFor, setQuickBusyFor] = useState(null); // tracking_id while upload in-flight
  const navigate = useNavigate();
  const lastTidsRef = useRef(null);
  const quickInputRef = useRef(null);
  const pendingTidRef = useRef(null);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [m, k, p] = await Promise.all([
        api.get("/packer/me"),
        api.get("/packer/kpis/today"),
        api.get("/packer/parcels"),
      ]);
      setMe(m.data || null);
      setKpis(k.data || {});
      const newRows = p.data?.rows || [];
      setRows(newRows);

      // Detect newly assigned parcels (delta against last snapshot).
      const tids = new Set(newRows.map((r) => r.tracking_id));
      if (lastTidsRef.current) {
        const added = [...tids].filter((t) => !lastTidsRef.current.has(t));
        added.forEach((t) => {
          toast("New parcel assigned", {
            description: `${t} · tap to open`,
            duration: 6000,
            action: { label: "Open", onClick: () => navigate(`/packer/parcel/${t}`) },
          });
        });
      }
      lastTidsRef.current = tids;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => { load(true); }, [load]);

  // Poll every 10 seconds
  useEffect(() => {
    const id = setInterval(() => load(false), 10_000);
    return () => clearInterval(id);
  }, [load]);

  const onQuickUploadClick = (tid) => {
    pendingTidRef.current = tid;
    quickInputRef.current?.click();
  };

  const onQuickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const tid = pendingTidRef.current;
    e.target.value = "";  // reset so re-selecting same file works
    pendingTidRef.current = null;
    if (!tid || files.length === 0) return;
    setQuickBusyFor(tid);
    try {
      // Ensure the parcel is in_progress — if it's still pending, /photos
      // is allowed in both states by the backend. We rely on the gate
      // there. If state advancement is needed, the user opens the detail.
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      await api.post(`/packer/parcels/${tid}/photos`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? "" : "s"} to ${tid}`, {
        action: { label: "Open", onClick: () => navigate(`/packer/parcel/${tid}`) },
      });
      await load(false);
    } catch (err) {
      toast.error(err?.userMessage || err?.response?.data?.detail || "Upload failed");
    } finally {
      setQuickBusyFor(null);
    }
  };

  return (
    <div className="max-w-[800px] mx-auto px-3 py-3 space-y-3" data-testid="packer-dashboard">
      {/* Hidden quick-upload file input — single input reused for every row */}
      <input ref={quickInputRef} type="file"
             accept="image/*"
             capture="environment"
             multiple
             onChange={onQuickFiles}
             style={{ display: "none" }}
             data-testid="packer-quick-upload-input"/>

      {/* My Hub card */}
      <MyHubCard hub={me?.hub}/>

      {/* Scan CTA */}
      <button onClick={() => setScanOpen(true)}
              data-testid="packer-scan-btn"
              className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl text-white shadow-sm hover:opacity-95 active:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#FF6B00]"
              style={{ backgroundColor: ORANGE }}>
        <ScanLine className="h-4 w-4"/> Scan parcel (QR / barcode)
      </button>

      {/* KPI strip — 3 columns on >=375, falls back to 1 col on the rare narrower viewport */}
      <div className="grid grid-cols-1 min-[375px]:grid-cols-3 gap-2" data-testid="packer-kpi-grid">
        <KpiCard label="Packed today"    value={kpis.packed_today}             testid="packer-kpi-packed-today"     Icon={Package} />
        <KpiCard label="In progress"     value={kpis.in_progress_now}          testid="packer-kpi-in-progress"      Icon={Boxes}   accentOrange={(kpis.in_progress_now || 0) > 0}/>
        <KpiCard label="Avg pack min"    value={kpis.avg_pack_minutes_today}   testid="packer-kpi-avg-min"          Icon={Clock} />
      </div>

      {/* Assigned parcels */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="packer-parcels-list">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Assigned parcels · {rows.length}</h2>
          <button onClick={() => load(false)}
                  data-testid="packer-refresh"
                  aria-label="Refresh"
                  className="text-slate-500 hover:text-[#FF6B00]">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
          </button>
        </div>
        {loading && rows.length === 0 && (
          <div className="px-3 py-8 text-center text-slate-400 text-sm">Loading…</div>
        )}
        {error && (
          <div className="px-3 py-6 text-center text-rose-600 text-sm" data-testid="packer-error">{error}</div>
        )}
        {!loading && rows.length === 0 && !error && (
          <div className="px-3 py-8 text-center text-slate-400 text-sm" data-testid="packer-empty">No parcels assigned yet.</div>
        )}
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => {
            const st = norm(r.packing_status);
            const pill = STATUS_PILL[st] || STATUS_PILL.pending;
            const canQuickUpload = st === "pending" || st === "in_progress";
            const busy = quickBusyFor === r.tracking_id;
            return (
              <li key={r.tracking_id}
                  data-testid={`packer-parcel-${r.tracking_id}`}
                  data-status={st}>
                <div className="flex items-stretch">
                  <button
                    onClick={() => navigate(`/packer/parcel/${r.tracking_id}`)}
                    className="flex-1 text-left px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-700 truncate">{r.tracking_id}</span>
                          <span className="text-[10px] text-slate-500">·</span>
                          <span className="text-xs text-slate-700 truncate">{(r.customer_name || "").split(" ")[0]}</span>
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                            {r.items_count} item{r.items_count === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Assigned {relTime(r.assigned_at)}</div>
                      </div>
                      <span data-testid={`packer-parcel-${r.tracking_id}-pill`}
                            className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border font-medium ${pill.cls}`}>
                        {pill.label}
                      </span>
                    </div>
                  </button>
                  {canQuickUpload && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onQuickUploadClick(r.tracking_id); }}
                      disabled={busy}
                      title="Upload packing proof photo (camera)"
                      data-testid={`packer-quick-upload-${r.tracking_id}`}
                      className="shrink-0 px-3 border-l border-slate-100 hover:bg-orange-50 active:bg-orange-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] disabled:opacity-50 inline-flex items-center gap-1 text-[#FF6B00] text-xs font-semibold">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Camera className="h-4 w-4"/>}
                      <span className="hidden min-[400px]:inline">Proof</span>
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <PackerScanModal open={scanOpen} onClose={() => setScanOpen(false)}/>
    </div>
  );
}

export default function PackerDashboard() {
  // APP-4 · Non-active packers (pending/rejected) belong on the splash screen.
  const status = (useAuthStore((s) => s.user?.approval_status) || "active").toLowerCase();
  if (status !== "active") return <Navigate to="/packer/pending" replace/>;
  return (
    <RoleGate allow={["packer_staff"]}>
      <PackerSubNav/>
      <Body/>
    </RoleGate>
  );
}
